import { useState } from 'react';
import PageHero from '../../components/PageHero';
import { Link } from 'react-router-dom';
import { footer } from '../../../config/appData';
import emailjs from '@emailjs/browser';
import './StaticPage.css';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_ENABLED = EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY;

function ContactUsPage() {
  const { contact, socialLinks } = footer;
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (EMAILJS_ENABLED) {
      setSending(true);
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            from_name: formData.name,
            from_email: formData.email,
            reply_to: formData.email,
            subject: formData.subject || 'Contact from WeddingzVenue',
            message: formData.message,
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } catch (err) {
        setError(err?.text || err?.message || 'Failed to send. Please try again or email us directly.');
      } finally {
        setSending(false);
      }
      return;
    }

    const { name, email, subject, message } = formData;
    const mailto = `mailto:${contact.email}?subject=${encodeURIComponent(subject || 'Contact from WeddingzVenue')}&body=${encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\n\n${message}`
    )}`;
    window.location.href = mailto;
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const phoneHref = contact.phone ? `tel:${contact.phone.replace(/\s/g, '')}` : null;

  return (
    <div className="static-page content-page contact-us-page">
      <PageHero
        title="Contact Us"
        subtitle="We're here to help with your wedding planning"
      />
      <div className="content-container contact-us-container">
        <p className="contact-us-intro">
          Have a question, feedback, or need support? Reach out to our team and we&apos;ll get back to you
          within 1–2 business days.
        </p>

        <div className="contact-us-grid">
          {/* Contact information */}
          <div className="contact-us-info">
            <h2 className="contact-us-section-title">Get in touch</h2>

            <div className="contact-info-cards">
              {phoneHref && (
                <a href={phoneHref} className="contact-info-card contact-info-card--link">
                  <span className="contact-info-icon" aria-hidden>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </span>
                  <div>
                    <h3>Phone</h3>
                    <span>{contact.phone}</span>
                  </div>
                </a>
              )}
              <a href={`mailto:${contact.email}`} className="contact-info-card contact-info-card--link">
                <span className="contact-info-icon" aria-hidden>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <div>
                  <h3>Email</h3>
                  <span>{contact.email}</span>
                </div>
              </a>
              <div className="contact-info-card">
                <span className="contact-info-icon" aria-hidden>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <div>
                  <h3>Address</h3>
                  <span>{contact.address}</span>
                </div>
              </div>
            </div>

            <div className="contact-us-help">
              <h3 className="contact-us-help-title">What we can help with</h3>
              <ul>
                <li>General questions about using WeddingzVenue</li>
                <li>Account or technical issues</li>
                <li>Feedback and suggestions</li>
                <li>Verification or listing concerns</li>
              </ul>
            </div>

            <div className="contact-us-social">
              <h3 className="contact-us-social-title">Follow us</h3>
              <div className="contact-us-social-links">
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="contact-us-social-link" aria-label="Facebook">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
                  </a>
                )}
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="contact-us-social-link" aria-label="Instagram">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
                  </a>
                )}
                {socialLinks.twitter && (
                  <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="contact-us-social-link" aria-label="X (Twitter)">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  </a>
                )}
                {socialLinks.youtube && (
                  <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="contact-us-social-link" aria-label="YouTube">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" /><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" /></svg>
                  </a>
                )}
                {socialLinks.whatsapp && (
                  <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="contact-us-social-link" aria-label="WhatsApp">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="contact-us-form-wrap">
            <h2 className="contact-us-section-title">Send a message</h2>
            {submitted ? (
              <div className="contact-us-success">
                <p>
                  {EMAILJS_ENABLED
                    ? "Thank you! Your message has been sent. We'll get back to you within 1–2 business days."
                    : "Your email client should open with the message ready to send. If it didn't, email us directly at "}
                  {!EMAILJS_ENABLED && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
                  {!EMAILJS_ENABLED && "."}
                </p>
                {EMAILJS_ENABLED && (
                  <button
                    type="button"
                    className="contact-us-form-submit contact-us-form-submit--secondary"
                    onClick={() => { setSubmitted(false); setError(null); }}
                  >
                    Send another message
                  </button>
                )}
              </div>
            ) : (
              <form className="contact-us-form" onSubmit={handleSubmit}>
                {error && (
                  <div className="contact-us-error" role="alert">
                    {error}
                  </div>
                )}
                <div className="contact-us-form-row">
                  <label htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    disabled={sending}
                  />
                </div>
                <div className="contact-us-form-row">
                  <label htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    disabled={sending}
                  />
                </div>
                <div className="contact-us-form-row">
                  <label htmlFor="contact-subject">Subject</label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this about?"
                    disabled={sending}
                  />
                </div>
                <div className="contact-us-form-row">
                  <label htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message..."
                    rows={5}
                    required
                    disabled={sending}
                  />
                </div>
                <button type="submit" className="contact-us-form-submit" disabled={sending}>
                  {sending ? 'Sending...' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="contact-us-more">
          <p>
            <strong>Venue or vendor enquiries?</strong> Use the enquiry form on the respective venue or vendor page.
          </p>
          <p>
            <strong>Business or partnership?</strong> Visit <Link to="/list-your-business">List Your Business</Link>,{' '}
            <Link to="/advertise">Advertise</Link>, or <Link to="/partners">Partners</Link>. For quick answers, check our <Link to="/faq">FAQ</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ContactUsPage;
